import * as React from 'react'

import { flexRender, Header, RowData, Table } from '@tanstack/react-table'

import { cn } from '../../utils/cn'
import { NorthIcon } from '../icons/NorthIcon'
import { SouthIcon } from '../icons/SouthIcon'
import {
  DEFAULT_COLUMN_MIN_SIZE,
  getAlignmentClass,
  getColumnMeta,
  getHeaderStyle,
  UNCAPPED_COLUMN_SIZE,
} from './columns'
import { STYLES } from './styles'

const DEFAULT_HEADER_HEIGHT = 40
const KEYBOARD_RESIZE_STEP = 16
const RESIZE_HANDLE_WIDTH = 12
const RESIZE_HANDLE_OFFSET = 6

interface IHeaderCellProps<TData extends RowData> {
  header: Header<TData, unknown>
  table: Table<TData>
  fillContainerWidth?: boolean
  // Shared clamped write path for deliberate widths; absent when the consumer
  // doesn't own width state, in which case keyboard resize writes transient
  // columnSizing directly.
  setColumnWidth?: (columnId: string, width: number) => void
}

export default function HeaderCell<TData extends RowData>({
  header,
  table,
  fillContainerWidth,
  setColumnWidth,
}: IHeaderCellProps<TData>): React.ReactElement {
  const meta = getColumnMeta(header.column)
  const canSort = header.column.getCanSort()
  const sorted = header.column.getIsSorted()
  const resizeHandler = header.getResizeHandler()
  const visibleLeafColumns = table.getVisibleLeafColumns()
  // Only when the table fills its container does the last column's right edge
  // meet the scroll edge — that's the case where a straddling handle overhangs.
  const isLastColumnAtEdge =
    !!fillContainerWidth &&
    visibleLeafColumns[visibleLeafColumns.length - 1]?.id === header.column.id

  const handleResizeKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
        return
      }

      event.preventDefault()
      event.stopPropagation()

      const delta =
        event.key === 'ArrowRight'
          ? KEYBOARD_RESIZE_STEP
          : -KEYBOARD_RESIZE_STEP

      const target = header.column.getSize() + delta

      // When the consumer owns width intent, persist through the shared clamped
      // setter; otherwise write transient columnSizing, clamping here.
      if (setColumnWidth) {
        setColumnWidth(header.column.id, target)
        return
      }

      const min = header.column.columnDef.minSize ?? DEFAULT_COLUMN_MIN_SIZE
      const max = header.column.columnDef.maxSize ?? UNCAPPED_COLUMN_SIZE
      table.setColumnSizing(old => ({
        ...old,
        [header.column.id]: Math.max(min, Math.min(max, target)),
      }))
    },
    [table, header.column, setColumnWidth],
  )

  const ariaSort = (() => {
    if (!canSort) {
      return undefined
    }

    if (sorted === 'asc') {
      return 'ascending' as const
    }

    if (sorted === 'desc') {
      return 'descending' as const
    }

    return 'none' as const
  })()

  const content = header.isPlaceholder ? null : canSort ? (
    <button
      type="button"
      className={cn(
        STYLES.sortableHeaderContent,
        getAlignmentClass(meta.align),
      )}
      onClick={header.column.getToggleSortingHandler()}
    >
      <span className="tw:flex-1 tw:overflow-hidden tw:text-ellipsis tw:whitespace-nowrap">
        {flexRender(header.column.columnDef.header, header.getContext())}
      </span>
      {sorted && (
        <span aria-hidden="true" className={STYLES.headerSortIcon}>
          {sorted === 'asc' ? <NorthIcon size={16} /> : <SouthIcon size={16} />}
        </span>
      )}
    </button>
  ) : (
    <div
      className={cn(STYLES.staticHeaderContent, getAlignmentClass(meta.align))}
    >
      {flexRender(header.column.columnDef.header, header.getContext())}
    </div>
  )

  return (
    <div
      role="columnheader"
      aria-colindex={Math.min(
        ...header.getLeafHeaders().map(h => h.column.getIndex() + 1),
      )}
      aria-colspan={header.colSpan}
      aria-sort={ariaSort}
      className={cn(
        STYLES.headerCell,
        getAlignmentClass(meta.align),
        meta.headerClassName,
      )}
      style={{
        minHeight: DEFAULT_HEADER_HEIGHT,
        ...getHeaderStyle(header),
      }}
    >
      {content}
      {header.column.getCanResize() &&
        !header.isPlaceholder &&
        header.colSpan === 1 && (
          <div
            role="separator"
            aria-label={`Resize ${header.column.id} column`}
            aria-orientation="vertical"
            tabIndex={0}
            className={cn(STYLES.resizeHandle, 'tw:group')}
            style={{
              // Keep the handle inside the cell only when it would otherwise
              // overhang the table's right edge (last column of a filled table)
              // and force a horizontal scrollbar.
              right: isLastColumnAtEdge ? 0 : -RESIZE_HANDLE_OFFSET,
              width: RESIZE_HANDLE_WIDTH,
            }}
            onMouseDown={resizeHandler}
            onTouchStart={resizeHandler}
            onKeyDown={handleResizeKeyDown}
          >
            <div
              className={cn(
                STYLES.resizeHandleLine,
                header.column.getIsResizing() && STYLES.resizeHandleLineActive,
              )}
            />
          </div>
        )}
    </div>
  )
}
